import Alert from '@mui/material/Alert';
import { CollapsibleCard } from 'modules/shared/components';
import { TASK_STATUS } from 'modules/work-implementation/constants';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { assignTaskToEmployee, deleteTaskAssignment, editTaskAssignment } from '../../services';
import { AssignedTaskView, TaskAssignmentForm } from '../components';
import { useTaskAssignment } from '../hooks/useTaskAssignment';
import { generateTaskList } from '../utils';

/**
 * TaskAssignment section
 * @param {Object} props
 * @param {{ taskConfig: Object, projectData: Object }} props.data
 * @param {Array<Object>} props.assignedTasks
 * @param {Function} props.refresh
 * @returns {JSX.Element}
 */
export default function TaskAssignment({ data, assignedTasks, refresh }) {
  const { projectId } = useParams();
  const [activeTaskKey, setActiveTaskKey] = useState(null);
  const isEditMode = activeTaskKey !== null;

  const { employeesByTaskType, loading: employeesLoading } = useTaskAssignment();

  const taskList = useMemo(
    () => generateTaskList(data.taskConfig, assignedTasks),
    [data.taskConfig, assignedTasks],
  );

  const handleActivate = useCallback((taskKey) => {
    setActiveTaskKey(taskKey);
  }, []);

  const handleCancel = useCallback(() => {
    setActiveTaskKey(null);
  }, []);

  const handleAssign = useCallback(
    async (payload) => {
      if (!projectId) return;
      try {
        await assignTaskToEmployee(projectId, payload);
        toast.success('Task assigned successfully');
        setActiveTaskKey(null);
        refresh();
      } catch (error) {
        toast.error(
          logError({
            message: 'Failed to assign task',
            error,
            code: 'TASK_ASSIGNMENT_FAILED',
          }),
        );
      }
    },
    [projectId, refresh],
  );

  const handleEdit = useCallback(
    async (taskId, payload) => {
      if (!taskId || !payload) return;
      try {
        await editTaskAssignment(taskId, payload);
        toast.success('Task edited successfully');
        setActiveTaskKey(null);
        refresh();
      } catch (error) {
        toast.error(
          logError({
            message: 'Failed to edit task',
            error,
            code: 'TASK_EDIT_FAILED',
          }),
        );
      }
    },
    [refresh],
  );

  const handleDelete = useCallback(
    async (taskId) => {
      if (!projectId) return;

      try {
        await deleteTaskAssignment(taskId);
        toast.success('Assignment deleted');
        setActiveTaskKey(null);
        refresh();
      } catch (error) {
        toast.error(
          logError({
            message: 'Failed to delete assignment',
            error,
            code: 'TASK_ASSIGNMENT_DELETE_FAILED',
          }),
        );
      }
    },
    [projectId, refresh],
  );

  return (
    <CollapsibleCard
      title='Work Summary'
      defaultExpanded
      borderVariant='outlined'
      colorVariant='default'
    >
      <div className='column gap-3'>
        {taskList.length === 0 && <Alert severity='info'>No tasks available for assignment</Alert>}
        {taskList.map((task) => {
          const availableEmployees = employeesByTaskType[task.type] || [];
          const isEditing = activeTaskKey === task.taskKey;
          const isLocked = isEditMode && activeTaskKey !== task.taskKey;

          if (task.status === TASK_STATUS.UNASSIGNED || isEditing) {
            return (
              <TaskAssignmentForm
                key={task.taskKey}
                task={task}
                projectData={data.projectData}
                taskList={taskList}
                assignedTask={(assignedTasks || []).find((t) => t.id === task.id)}
                isActive={isEditing}
                isLocked={isLocked}
                onActivate={(taskKey) => {
                  if (isEditMode) return;
                  handleActivate(taskKey);
                }}
                onCancel={handleCancel}
                onAssign={handleAssign}
                onEdit={handleEdit}
                employees={availableEmployees}
                isLoading={employeesLoading}
              />
            );
          }

          return (
            <AssignedTaskView
              key={task.taskKey}
              taskMetadata={task}
              task={(assignedTasks || []).find((t) => t.id === task.id)}
              projectData={data.projectData}
              isLocked={isLocked}
              onEdit={(taskKey) => {
                if (isLocked) return;
                setActiveTaskKey(taskKey);
              }}
              onDelete={(taskId) => {
                if (isLocked) return;
                handleDelete(taskId);
              }}
            />
          );
        })}
      </div>
    </CollapsibleCard>
  );
}
